import { orderBy, sumBy } from 'lodash-es'
import { defineStore } from 'pinia'
import { markRaw } from 'vue'

import { maybeShowChatNotification } from '../services/chat_utils/chat_utils.js'
import { pollingActions } from '../services/poller/poller.js'
import { createStreamedPoller } from '../services/streamed_poller/streamed_poller.js'

import { useInterfaceStore } from 'src/stores/interface.js'
import { useStreamingStore } from 'src/stores/streaming.js'
import { useUsersStore } from 'src/stores/users.js'

import { chats, pinChat, unpinChat } from 'src/api/chats.js'

const defaultState = {
  data: new Map(),
  poller: null,
  // runs the poller while the stream is down
  streamPoller: null,
}

const polling = pollingActions((store) => store.fetchChats(), 5000)

// chats have no cursor to page from: every connect fetches them again
const streamedPoller = (store) => {
  store.streamPoller ??= markRaw(
    createStreamedPoller({
      start: () => polling.startFetching.call(store),
      stop: () => polling.stopFetching.call(store),
      catchUp: () => store.fetchChats(),
      catchUpOn: 'connect',
    }),
  )
  return store.streamPoller
}

export const useChatsStore = defineStore('chats', {
  state: () => ({ ...defaultState }),
  getters: {
    sortedChatList(state) {
      return orderBy(
        [...state.data.values()],
        ['pinned', 'updated_at'],
        ['desc', 'desc'],
      )
    },
    unreadChatsCount(state) {
      return sumBy([...state.data.values()], 'unread')
    },
  },
  actions: {
    attachSocket() {
      const et = new EventTarget()
      const socket = {
        name: 'chats',
        et,
      }

      et.addEventListener('pleroma:chat_update', ({ data: { chatUpdate } }) => {
        this.updateChat(chatUpdate)
      })
      et.addEventListener('open', () => streamedPoller(this).streamUp())
      et.addEventListener('close', () => streamedPoller(this).streamDown())

      useStreamingStore().addSubscriber(socket)
    },
    startFetching() {
      streamedPoller(this).want(true)
    },
    stopFetching() {
      streamedPoller(this).want(false)
    },
    async fetchChats() {
      this.addNewChats(await chats({}))
    },
    resetChats() {
      this.data = new Map()
    },
    addNewChats(result) {
      useUsersStore().addNewUsers({
        ...result,
        data: result.data.map((k) => k.account).filter(Boolean),
      })

      // We do unshift in update so we reverse the chat list here
      result.data.forEach((chat) => this.updateChat(chat))
    },
    readChat(id) {
      const chat = this.data.get(id)
      if (chat) {
        chat.unread = 0
      } else {
        console.error(`Chat ${id} not found!`)
      }
    },
    async togglePin(id) {
      const chat = this.data.get(id)
      if (!chat || chat.pinning) return
      const wasPinned = chat.pinned
      // shown at once, set back if the server refuses
      chat.pinned = !wasPinned
      chat.pinning = true
      try {
        const call = wasPinned ? unpinChat : pinChat
        const { data } = await call({ id })
        chat.pinned = !!data.pinned
      } catch {
        chat.pinned = wasPinned
        useInterfaceStore().pushGlobalNotice({
          level: 'error',
          messageKey: 'chats.pin_error',
        })
      } finally {
        chat.pinning = false
      }
    },
    updateChat(updatedChat) {
      const chat = this.data.get(updatedChat.id)
      if (chat) {
        const isNewMessage =
          chat.lastMessage?.id !== updatedChat.lastMessage?.id
        chat.lastMessage = updatedChat.lastMessage
        chat.unread = updatedChat.unread
        chat.updated_at = updatedChat.updated_at
        chat.pinned = updatedChat.pinned
        if (isNewMessage) maybeShowChatNotification(chat)
      } else {
        this.data.set(updatedChat.id, updatedChat)
        maybeShowChatNotification(updatedChat)
      }
    },
  },
})
